import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { X, FileText, Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;
export const fileUrl = (id, inline) => `${API}/api/chats/files/${id}${inline ? "?inline=true" : ""}`;
const OK_RE = /\.(pdf|docx?|xlsx?|pptx?|csv|txt|png|jpe?g|gif|webp)$/i;
const MAX = 10 * 1024 * 1024;
export const MAX_FILES = 5;
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Pending uploads for the composer: each file uploads as soon as it's added
export function useChatUploads(chatId) {
  const [pending, setPending] = useState([]);
  const add = (fileList) => {
    let files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_FILES - pending.length;
    if (files.length > room) { toast.error(`You can attach up to ${MAX_FILES} files per message`); files = files.slice(0, Math.max(0, room)); }
    files.forEach((f) => {
      const name = f.name && f.name !== "image.png" ? f.name : `pasted-${Date.now()}.${(f.type.split("/")[1] || "png").replace("jpeg", "jpg")}`;
      if (!OK_RE.test(name)) { toast.error(`${name}: file type not supported`); return; }
      if (f.size > MAX) { toast.error(`${name} exceeds the 10 MB limit`); return; }
      const key = `${Date.now()}-${Math.random()}`;
      const isImage = /^image\//.test(f.type);
      setPending((p) => [...p, { key, name, size: f.size, isImage, preview: isImage ? URL.createObjectURL(f) : null, id: null }]);
      const fd = new FormData();
      fd.append("file", f, name);
      client.post(`/chats/${chatId}/files`, fd)
        .then(({ data }) => setPending((p) => p.map((x) => (x.key === key ? { ...x, id: data.id } : x))))
        .catch((e) => { toast.error(`${name}: ${e?.response?.data?.detail || "upload failed"}`); setPending((p) => p.filter((x) => x.key !== key)); });
    });
  };
  const remove = (key) => setPending((p) => p.filter((x) => x.key !== key));
  const clear = () => setPending([]);
  const uploading = pending.some((x) => !x.id);
  return { pending, add, remove, clear, uploading, ids: pending.filter((x) => x.id).map((x) => x.id) };
}

export function PendingFiles({ pending, onRemove }) {
  if (!pending.length) return null;
  return (
    <div className="mb-1.5 flex flex-wrap gap-2" data-testid="chat-pending-files">
      {pending.map((p) => (
        <div key={p.key} className="relative" data-testid="chat-pending-file">
          {p.isImage ? (
            <img src={p.preview} alt={p.name} className={cn("h-14 w-14 rounded-lg object-cover ring-1 ring-slate-200", !p.id && "opacity-50")} />
          ) : (
            <div className={cn("flex h-14 w-40 items-center gap-1.5 rounded-lg bg-slate-50 px-2 ring-1 ring-slate-200", !p.id && "opacity-50")}>
              <FileText className="h-4 w-4 shrink-0 text-sky-600" />
              <span className="min-w-0 truncate text-[11px] font-medium text-slate-700">{p.name}</span>
            </div>
          )}
          {!p.id && <Loader2 className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 animate-spin text-sky-600" />}
          <button onClick={() => onRemove(p.key)} aria-label="Remove file" data-testid="chat-pending-remove"
            className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-700 p-0.5 text-white shadow hover:bg-slate-900"><X className="h-3 w-3" /></button>
        </div>
      ))}
    </div>
  );
}

export function MessageFiles({ files, mine }) {
  const [open, setOpen] = useState(null);
  if (!files?.length) return null;
  const images = files.filter((f) => f.is_image);
  const docs = files.filter((f) => !f.is_image);
  return (
    <div className="mb-1 w-60 max-w-full space-y-1" data-testid="chat-message-files">
      {images.length > 0 && (
        <div className={cn("grid gap-1", images.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
          {images.map((f) => (
            <button key={f.id} type="button" onClick={() => setOpen(f)} data-testid={`chat-image-${f.id}`} className="block overflow-hidden rounded-lg bg-slate-100">
              <img src={fileUrl(f.id, true)} alt={f.name} loading="lazy" className="max-h-48 w-full object-cover transition-transform hover:scale-[1.02]" />
            </button>
          ))}
        </div>
      )}
      {docs.map((f) => (
        <a key={f.id} href={fileUrl(f.id)} data-testid={`chat-file-${f.id}`}
          className={cn("flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-xs no-underline", mine ? "bg-white/15 text-white hover:bg-white/25" : "bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100")}>
          <FileText className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate font-medium">{f.name}</span>
          <span className="shrink-0 opacity-70">{fmtSize(f.size)}</span>
          <Download className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </a>
      ))}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl p-3" data-testid="chat-image-viewer">
          <DialogTitle className="truncate pr-8 text-sm font-medium text-slate-700">{open?.name}</DialogTitle>
          {open && <img src={fileUrl(open.id, true)} alt={open.name} className="max-h-[75vh] w-full rounded-md object-contain" />}
          {open && (
            <a href={fileUrl(open.id)} data-testid="chat-image-download" className="ml-auto inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-sky-700 hover:bg-sky-50">
              <Download className="h-4 w-4" /> Download
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
